import { supabase } from "@/integrations/supabase/client";
import { OrderWithRelations } from "@/components/schedule/types";
import { mapEapiStatusToOrderStatus } from "@/lib/eapi-status-mapper";
import { parentStatusForLeg } from "@/lib/leg-status-resolve";
import type { OrderStatus } from "@/types/tms";

// Local rank used to pick the most-progressed status without regressing.
// Mirrors STATUS_RANK in eapi-status-mapper but kept local to avoid an export.
const LEG_STATUS_RANK: Record<string, number> = {
  booked: 0,
  confirmed: 1,
  tender_received: 2,
  in_transit: 3,
  delivered: 4,
  canceled: 5,
};

function pickFurthest(...statuses: Array<string | null | undefined>): OrderStatus | undefined {
  let best: string | undefined;
  let bestRank = -1;
  for (const s of statuses) {
    if (!s) continue;
    const r = LEG_STATUS_RANK[s] ?? -1;
    if (r > bestRank) {
      bestRank = r;
      best = s;
    }
  }
  return best as OrderStatus | undefined;
}

/**
 * Phase 4 read-path helper: fetches per-leg rows from `orders_with_legs`
 * with the standard relation embeds, and re-shapes them onto the legacy
 * `OrderWithRelations` field names so existing UI consumers (timelines,
 * boards, dashboards, driver assignments) keep working unchanged.
 *
 * - `id` is aliased to `leg_id` (one row per leg → one card per leg).
 * - `parent_order_id` is aliased to the view's `order_id` (the parent
 *   order id). This matches the convention used by `useScheduleData`.
 * - `created_at`/`updated_at` collapse from the view's split columns.
 */
export const ORDERS_WITH_LEGS_SELECT = `
  *,
  billing_party:customers(*)
`;

export function mapLegRowToOrder(row: any): OrderWithRelations {
  return {
    ...row,
    id: row.leg_id,
    parent_order_id: row.order_id,
    created_at: row.order_created_at,
    updated_at: row.leg_updated_at ?? row.order_updated_at,
  } as OrderWithRelations;
}

export function mapLegRowsToOrders(rows: any[] | null | undefined): OrderWithRelations[] {
  return (rows ?? []).map(mapLegRowToOrder);
}

/**
 * Async helper: maps and hydrates tractor/driver relations in one call.
 * Prefer this over `mapLegRowsToOrders` for all read-paths that need
 * `assigned_tractor` / `assigned_operator` populated.
 */
export async function mapAndHydrateLegRows(
  rows: any[] | null | undefined,
): Promise<OrderWithRelations[]> {
  return hydrateLegRelations(mapLegRowsToOrders(rows));
}

/**
 * Hydrate tractor/driver relations on leg rows. The `orders_with_legs` view
 * has no FK relationships to `tractors`/`drivers` in the PostgREST schema
 * cache, so embedded selects fail (PGRST200). Fetch them in a second pass.
 */
export async function hydrateLegRelations(
  rows: OrderWithRelations[],
): Promise<OrderWithRelations[]> {
  if (!rows.length) return rows;
  const tractorIds = Array.from(
    new Set(rows.map((r: any) => r.assigned_tractor_id).filter(Boolean)),
  ) as string[];
  const driverIds = Array.from(
    new Set(
      rows.flatMap((r: any) => [r.assigned_operator_id, r.assigned_operator_2_id]).filter(Boolean),
    ),
  ) as string[];
  const legIds = Array.from(
    new Set(rows.map((r: any) => r.id).filter(Boolean)),
  ) as string[];

  const [tractorsRes, driversRes, legsRes] = await Promise.all([
    tractorIds.length
      ? supabase.from("tractors").select("*").in("id", tractorIds)
      : Promise.resolve({ data: [] as any[] }),
    driverIds.length
      ? supabase.from("drivers").select("*").in("id", driverIds)
      : Promise.resolve({ data: [] as any[] }),
    // Hydrate per-leg `driver_status` so leg cards reflect the driver's
    // own progression (Ready to Depart → I've Arrived) on multi-leg orders,
    // independent of the parent order's aggregated status.
    legIds.length
      ? supabase.from("legs").select("id, driver_status").in("id", legIds)
      : Promise.resolve({ data: [] as any[] }),
  ]);
  const tractorsById = new Map((tractorsRes.data ?? []).map((t: any) => [t.id, t]));
  const driversById = new Map((driversRes.data ?? []).map((d: any) => [d.id, d]));
  const legStatusById = new Map(
    ((legsRes as any).data ?? []).map((l: any) => [l.id, l.driver_status as string | null]),
  );

  // Identify multi-leg parents in this batch. For a single detail-page
  // load we only have one row, so also probe the DB for sibling counts of
  // any parent we haven't already proven multi-leg locally.
  const parentLegCounts = new Map<string, number>();
  for (const r of rows as any[]) {
    const pid = r.parent_order_id as string | undefined;
    if (!pid) continue;
    parentLegCounts.set(pid, (parentLegCounts.get(pid) ?? 0) + 1);
  }
  const unresolvedParents = Array.from(parentLegCounts.entries())
    .filter(([, n]) => n < 2)
    .map(([pid]) => pid);
  if (unresolvedParents.length) {
    const { data: siblingRows } = await supabase
      .from("legs")
      .select("order_id")
      .in("order_id", unresolvedParents);
    for (const s of (siblingRows ?? []) as Array<{ order_id: string }>) {
      parentLegCounts.set(s.order_id, (parentLegCounts.get(s.order_id) ?? 0) + 1);
      // The above double-counts rows already in `parentLegCounts` from the
      // input batch, but we only need >=2 as the threshold so overshoot is
      // harmless.
    }
  }

  return rows.map((r: any) => {
    const hasLegRow = legStatusById.has(r.id);
    const legStatus = legStatusById.get(r.id);
    const eapiRaw = r.eapi_status ? mapEapiStatusToOrderStatus(String(r.eapi_status)) : null;
    // An upstream-cancelled trip is not a cancelled leg (same rule as
    // `resolveLegStatus`): cancellation only comes from ATMS.
    const eapiMapped = eapiRaw === "canceled" ? null : eapiRaw;
    // Resolve the leg-level `status` we expose to the UI. Same parent rule
    // as `resolveLegStatus`: on a multi-leg order the parent is a rollup off
    // SOME leg and contributes only `canceled` to this one, so a sibling leg
    // can never be dragged forward by another leg's progress; a leg with no
    // signal of its own reads `booked`. Single-leg parents and legacy rows
    // with no `legs` entry pass the stored status straight through.
    const parentIsMultiLeg =
      (parentLegCounts.get(r.parent_order_id as string) ?? 0) > 1;
    const legIsMultiLeg = hasLegRow && parentIsMultiLeg;
    const parentContribution = parentStatusForLeg(r.status as string | undefined, legIsMultiLeg);
    const resolved =
      (hasLegRow
        ? pickFurthest(parentContribution, legStatus as string | null | undefined, eapiMapped)
        : pickFurthest(parentContribution, eapiMapped)) ??
      (legIsMultiLeg ? ("booked" as OrderStatus) : undefined);
    return {
      ...r,
      // Overlay leg-level progression (driver action + EAPI trip status)
      // on top of the parent order status so leg-specific UI targets THIS
      // leg. Only forward progression — never regress the visible state.
      ...(resolved ? { status: resolved } : {}),
      assigned_tractor: r.assigned_tractor_id ? tractorsById.get(r.assigned_tractor_id) ?? null : null,
      assigned_operator: r.assigned_operator_id ? driversById.get(r.assigned_operator_id) ?? null : null,
      assigned_operator_2: r.assigned_operator_2_id
        ? driversById.get(r.assigned_operator_2_id) ?? null
        : null,
    };
  });
}

/** Re-export of the view name so call sites read clearly. */
export const ORDERS_WITH_LEGS_VIEW = "orders_with_legs" as const;

/**
 * Resolve the parent ORDER id for a row that may be either a real `orders`
 * row or a leg row from `orders_with_legs` (where `id` is the leg id and
 * `parent_order_id` holds the order id).
 *
 * Any UI that queries order-scoped tables (`order_notes`, `order_bol_images`,
 * `legs.order_id`, ...) MUST go through this — passing a leg id there returns
 * no rows and surfaces as "Could not resolve this order's workspace".
 */
export function resolveParentOrderId(
  row: { id?: string | null; parent_order_id?: string | null } | null | undefined,
): string | null {
  if (!row) return null;
  return row.parent_order_id ?? row.id ?? null;
}